import type { CSSProperties, ElementType, ReactNode } from 'react'
import { Glass } from './Glass'

export type TomDoBotao = 'primario' | 'secundario' | 'perigo'

type Props = {
  tom?: TomDoBotao
  /**
   * O elemento. `button` por padrão; `Link` quando a ação é ir para outro
   * lugar.
   *
   * **Voltar é ação de tela como qualquer outra** — uma por viewport, e a
   * única saída de uma tela empilhada —, mas é uma navegação, e navegação é
   * `<a>`: só ela abre em outra aba, mostra o destino na barra e é anunciada
   * como link. Um `<button>` que navega parece igual e mente sobre o que faz.
   */
  as?: ElementType
  /**
   * Desfoca o que passa atrás. **Caro, e por isso opt-in** — ver o orçamento
   * abaixo. Reservado para o botão que é a ação principal de uma tela.
   */
  desfoca?: boolean
  children: ReactNode
  className?: string
  /**
   * Vence o `style` daqui. **É a única forma de mudar espaçamento, cor de
   * fundo ou raio deste botão** — ver a última seção do bloco abaixo.
   */
  style?: CSSProperties
  [prop: string]: unknown
}

/**
 * O botão de ação do app: pílula de vidro.
 *
 * ## Um lugar só para a decisão, porque ela já estava em cinco
 *
 * "Salvar produto" era `bg-[var(--brand)] rounded-full`, "Cancelar" era texto
 * apagado, "Novo produto" era vidro, "Registrar preço" era sublinhado. Quatro
 * respostas para "com que cara um botão aparece aqui", e nenhuma delas errada
 * sozinha — o problema é serem quatro.
 *
 * ## Vidro NÃO é para todo botão, e o motivo é medido
 *
 * `Glass` documenta o orçamento: **no máximo 6 `frost` na viewport, nunca em
 * item de lista** — passar disso derrubou a composição do Chromium num
 * protótipo, com a página inteira em branco. Os ✕, ⏰ e ⠿ de cada linha de
 * lembrete são dezenas por tela.
 *
 * Então a regra é: **ação de TELA usa este componente; controle de LINHA não.**
 * Um botão que existe uma vez por tela — salvar, cancelar, criar, confirmar —
 * é de vidro. Um que se repete por item continua sendo ícone ou texto.
 *
 * ## E mesmo entre eles, o desfoque é opt-in
 *
 * O padrão é `flat`: mesma pílula, mesma borda, mesmo rim specular, mesma
 * sombra — **sem `backdrop-filter`**. É o desfoque que custa no compositor, não
 * o formato, e num botão de 44 px ele quase não aparece.
 *
 * Isto não é cautela abstrata: convertendo os pares Salvar/Cancelar, a tela do
 * produto com o formulário de preço aberto **foi a 7 `frost`** — medido no
 * navegador, com o teto em 6. Com `flat` ela volta a 5. O `desfoca` fica para
 * o botão que abre a tela, um por viewport.
 *
 * ## O tint vai por `style`, e isso não é preguiça
 *
 * `glass.css` é importado sem `@layer` e vence as utilities do Tailwind:
 * `.glass` já declara `background` e `border-radius`, e um `bg-*` ou
 * `rounded-full` ao lado é **escrito e ignorado** — medido nas decisões 223 e
 * 230. Qualquer propriedade que o `.glass` declare precisa vir por `style`.
 *
 * ## E o `px-4` daqui vence o `px-0` de quem chama, pelo mesmo motivo
 *
 * Entre duas utilities do Tailwind quem decide é a ordem da **folha gerada**,
 * não a ordem do atributo `class`: `px-0` escrito depois continua perdendo para
 * o `px-4` que esta linha declara. Medido em produção — um botão de ícone com
 * `w-11 px-0` computava `padding: 16px`, sobravam 10px de caixa, e o `<svg>`,
 * sendo item flex, encolhia de 24px para 10px sem erro nenhum.
 *
 * Por isso o `style` de quem chama entra **por último** e vence o daqui: é o
 * caminho que funciona, e o mesmo que a seção acima já exigia para o vidro.
 */
export function Botao({
  tom = 'secundario',
  desfoca = false,
  as = 'button',
  children,
  className = '',
  style,
  ...rest
}: Props) {
  const cor = {
    primario: 'font-medium text-[var(--bg)]',
    secundario: 'text-[var(--brand)]',
    perigo: 'font-medium text-[var(--bg)]',
  }[tom]

  const fundo = {
    primario: 'color-mix(in oklab, var(--brand) 82%, transparent)',
    secundario: undefined,
    perigo: 'color-mix(in oklab, var(--negative) 78%, transparent)',
  }[tom]

  return (
    <Glass
      {...rest}
      level={desfoca ? 'frost' : 'flat'}
      as={as}
      // Desabilitado SE LÊ desabilitado — decisão 311. Antes ficava com a cor
      // cheia da marca, e o toque não fazia nada nem dizia por quê.
      className={`alvo gap-1.5 px-4 text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${cor} ${className}`.trim()}
      style={{
        borderRadius: '9999px',
        ...(fundo === undefined ? {} : { background: fundo }),
        ...style,
      }}
    >
      {children}
    </Glass>
  )
}
